import uniqBy from 'lodash/uniqBy'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useTw } from '../../../../../../utils/Styles'
import { Avatars } from '../../../../../../utils/Avatars'
import { useProfile } from '../../../../../../utils/Profile'
import { Button, ButtonLink } from '../../../../../../utils/UI'
import { AuthenticationType } from '../../../../../../api/types'
import { getSchedule } from '../../../../../../utils/WorkWeekSchedule'
import { useCreateConversationMutation } from './CreateConversation.generated'
import { ConversationFragmentDoc } from '../../../../../../api/Fragments/Conversation.generated'

function OnlineStatus({ open }: { open: boolean }) {
  const tw = useTw()
  const [t] = useTranslation('common')
  return (
    <div className={tw`flex ml-3 items-center`}>
      <div
        className={tw`
          w-3	h-3 mr-1.5 rounded-full
          ${open ? 'bg-success' : 'bg-neutral-350'}`}
      />
      <p className={tw`select-none text-neutral-350 text-sm`}>
        {t(
          open
            ? 'home.conversation.new_conversation.schedule_status.open'
            : 'home.conversation.new_conversation.schedule_status.closed'
        )}
      </p>
    </div>
  )
}

type Props = {
  showMore: boolean
}

export function NewConversation({ showMore }: Props) {
  const tw = useTw()
  const profile = useProfile()
  const navigate = useNavigate()
  const [t] = useTranslation('common')
  const schedule = getSchedule(profile.inbox.workWeek)
  const day = t(`common:days.${schedule?.day}`)
  const [createConvo, { loading }] = useCreateConversationMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['Conversations'],
  })

  return (
    <div className={tw`p-4 w-full`}>
      <p className={tw`text-sm pb-2 font-bold`}>
        {t('home.conversation.new_conversation.title')}
      </p>
      <div className={tw`flex items-center py-1`}>
        <Avatars users={profile.inbox.users} />
        <OnlineStatus open={schedule?.open} />
      </div>
      {schedule.schedule ? (
        <div className={tw`text-sm pr-14 py-2`}>
          {t(
            `home.conversation.new_conversation.schedule_hours.${schedule.type}`,
            {
              schedule: schedule.schedule,
              day,
            }
          )}
        </div>
      ) : null}
      <div className={tw`flex items-center justify-center py-2`}>
        <Button
          strong
          disabled={loading}
          text={t('home.conversation.new_conversation.new_chat_button')}
          onClick={() =>
            profile.allowGuests &&
            profile.visitor.type === AuthenticationType.Unauthenticated
              ? navigate(`conversations/guest-setup`)
              : createConvo({
                  update(cache, { data }) {
                    cache.modify({
                      fields: {
                        Conversations(existing = []) {
                          const ref = cache.writeFragment({
                            data: data.CreateConversation,
                            fragmentName: 'Conversation',
                            fragment: ConversationFragmentDoc,
                            id: cache.identify(data.CreateConversation),
                          })

                          return uniqBy([ref, ...existing], '__ref')
                        },
                      },
                    })
                  },
                }).then((res) => {
                  navigate(`conversation/${res.data.CreateConversation._id}`)
                })
          }
        />
      </div>
      {showMore ? (
        <div className={tw`text-center pt-2`}>
          <ButtonLink
            onClick={() => navigate('/conversations')}
            text={t('home.conversation.new_conversation.previous_convo')}
          />
        </div>
      ) : null}
    </div>
  )
}
